import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { Order, CreateOrderDto, OrderStatus } from '@/lib/types';

interface OrdersResponse {
  data: Order[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export function useOrders(options?: { page?: number; limit?: number }) {
  const { page, limit } = options || {};
  
  return useQuery({
    queryKey: ['/orders', { page, limit }],
    queryFn: async (): Promise<OrdersResponse> => {
      const params = new URLSearchParams();
      if (page) params.append('page', String(page));
      if (limit) params.append('limit', String(limit));
      
      const endpoint = params.toString() ? `/orders?${params.toString()}` : '/orders';
      const response = await apiClient.get(endpoint);
      if (!response.ok) {
        throw new Error('Failed to fetch orders');
      }
      const result = await response.json();
      // Handle both { data: Order[], pagination } and direct Order[] response
      if (Array.isArray(result)) {
        return { data: result };
      }
      return result;
    },
    enabled: !!apiClient.getToken(),
  });
}

export function useUpcomingOrders() {
  return useQuery({
    queryKey: ['/orders', 'upcoming'],
    queryFn: async (): Promise<Order[]> => {
      const response = await apiClient.get('/orders/upcoming');
      if (!response.ok) {
        throw new Error('Failed to fetch upcoming orders');
      }
      const result = await response.json();
      // Handle both { data: Order[] } and direct Order[] response
      return result.data || result;
    },
    enabled: !!apiClient.getToken(),
  });
}

export function useOrder(id: string) {
  return useQuery({
    queryKey: ['/orders', id],
    queryFn: async (): Promise<Order> => {
      const response = await apiClient.get(`/orders/${id}`);
      if (!response.ok) {
        throw new Error('Failed to fetch order');
      }
      const result = await response.json();
      // Handle both { data: Order } and direct Order response
      return result.data || result;
    },
    enabled: !!id && !!apiClient.getToken(),
  });
}

export function useCreateOrder() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (orderData: CreateOrderDto): Promise<Order> => {
      const response = await apiClient.post('/orders', orderData);
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create order');
      }
      const data = await response.json();
      // Handle both wrapped { order: Order } and direct Order response
      return data.order || data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/orders'] });
    },
  });
}

// NOTE: useUpdateOrderStatus was removed along with the server's customer
// `PATCH /orders/:id/status` endpoint — it allowed arbitrary status
// transitions (see orders.controller). Cancellation goes through useCancelOrder.

export function useCancelOrder() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason?: string }): Promise<Order> => {
      // The server route is PATCH and reads `cancellationReason` — this used
      // to POST `{ reason }`, which 404ed and dropped the reason.
      const response = await apiClient.patch(`/orders/${id}/cancel`, { cancellationReason: reason });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to cancel order');
      }
      const result = await response.json();
      return result.data || result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/orders'] });
    },
  });
}

export function useEditOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, addressId, timeSlotId, paymentMethod }: {
      id: string;
      addressId?: string;
      timeSlotId?: string;
      paymentMethod?: 'knet' | 'credit_card' | 'apple_pay';
    }): Promise<Order & { requiresPayment?: boolean; paymentLink?: string; supplementalPaymentId?: string }> => {
      const response = await apiClient.patch(`/orders/${id}/edit`, { addressId, timeSlotId, paymentMethod });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update order');
      }
      const result = await response.json();
      return result.data || result;
    },
    onSuccess: (data) => {
      // Only invalidate if the edit completed (not when a payment redirect is required).
      if (!data?.requiresPayment) {
        queryClient.invalidateQueries({ queryKey: ['/orders'] });
      }
    },
  });
}
